import type { TFunction } from 'i18next';
import api from '../../../services/api';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import type { OrderCostPlanLineStock, OrderCostPlanPendingReason } from '../../../types/orderWarehouseSummary';

export interface OrderProductPayload {
  id?: string;
  productVariantId?: string | null;
  name: string;
  quantity: number;
  saleUnit?: QuantityUnit;
  saleQuantity?: number;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  unitPrice?: number;
  currency?: string;
  unit: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  description?: string;
  cnCode?: string;
  netWeight?: number;
  grossWeight?: number;
  countryOfOrigin?: string;
}

/**
 * An order with an approved sales invoice answers 409 with the shared
 * operational-exception contract (ORDER_CHANGED_AFTER_INVOICE, WP1 part 2).
 * Pass `confirmation` from a page that has `modal`/`t`; a cancelled
 * confirmation resolves to `false` and nothing was saved.
 */
export interface OrderSaveConfirmation {
  modal: Parameters<typeof submitWithOperationalException>[2];
  t: TFunction;
}

export async function saveOrderProducts(
  orderId: string,
  products: OrderProductPayload[],
  currency?: string,
  confirmation?: OrderSaveConfirmation,
): Promise<boolean> {
  const payload: Record<string, unknown> = { products, currency };
  if (!confirmation) {
    await api.put(`/orders/${orderId}/products`, payload);
    return true;
  }
  const response = await submitWithOperationalException(
    (body) => api.put(`/orders/${orderId}/products`, body),
    payload,
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

export interface OrderFifoPreviewLine {
  id: string;
  /** Absent for the sales rep (planned margin only: marginPercent + costBasis). */
  purchaseUnitPrice?: number | null;
  costPLN?: number;
  marginPLN?: number | null;
  marginPercent: number | null;
  hasShortage: boolean;
  /** Cost source label: warehouse FIFO, part FIFO + part average cost (mixed), the average cost of sales only, or unknown. */
  costBasis?: OrderLineCostBasis;
  /** Present for full-margin roles only; the sales rep never gets the FIFO/average split. */
  averageCostPLN?: number | null;
  /** Average purchase cost per unit (PLN) — full-margin roles only. */
  averagePurchasePricePLN?: number | null;
  averagePurchasePriceUnit?: string | null;
  /** Why the line cost is unknown (quantities/reason only, also for the sales rep). */
  pendingReason?: OrderCostPlanPendingReason | null;
  /** Queue explanation in the line unit (quantities only). */
  stock?: OrderCostPlanLineStock | null;
}

export type OrderLineCostBasis = 'WAREHOUSE' | 'MIXED' | 'AVERAGE_ESTIMATE' | 'UNKNOWN';
const ORDER_LINE_COST_BASES: readonly string[] = ['WAREHOUSE', 'MIXED', 'AVERAGE_ESTIMATE', 'UNKNOWN'];

export async function fetchOrderFifoPreview(orderId: string, products: unknown[], saved = false): Promise<OrderFifoPreviewLine[]> {
  const { data } = await api.post(`/orders/${orderId}/fifo-preview`, { products, saved });
  if (!Array.isArray(data) || data.some(line => !line || typeof line.id !== 'string'
    || (line.costPLN !== undefined && !(typeof line.costPLN === 'number' && Number.isFinite(line.costPLN)))
    || (line.marginPercent !== null && !(typeof line.marginPercent === 'number' && Number.isFinite(line.marginPercent)))
    || !['purchaseUnitPrice', 'marginPLN'].every(key => line[key] === undefined || line[key] === null || (typeof line[key] === 'number' && Number.isFinite(line[key])))
    || (line.averageCostPLN !== undefined && line.averageCostPLN !== null && (typeof line.averageCostPLN !== 'number' || !Number.isFinite(line.averageCostPLN)))
    || (line.costBasis !== undefined && !ORDER_LINE_COST_BASES.includes(line.costBasis))
    || typeof line.hasShortage !== 'boolean')) throw new Error('Invalid FIFO preview');
  return data;
}
